import { Inject, Injectable } from '@angular/core';
import { VendorSalesRep } from '../models/vendorSalesRep.model';
import { ENV, IEnvironment } from 'env/config';
import { BehaviorSubject, combineLatest, Observable, of } from 'rxjs';
import { concatMap, map, switchMap, take, tap } from 'rxjs/operators';

import { Service } from '../entity';
import { DataService } from '../http/dataService';
import { Columns, Result } from '../interfaces/helpers';
import { CentralApis, ConfigColumns } from '../interfaces/internal';
import { IKnock, Knock, BaseKnock } from '../models/knock.model';
import { Repository } from './repository';
import { VendorSalesRepService } from './vendorSalesRep';

@Service({
  api: 'knock',
  model: Knock,
})
@Injectable({ providedIn: 'root' })
export class KnockService extends Repository<Knock> {
  tableFieldsToShown = [];
  protected columnsVisibility = new BehaviorSubject<Columns[]>([]);
  protected baseColumnsVisibility = new BehaviorSubject<Columns[]>([]);

  constructor(
    @Inject(ENV) env: IEnvironment,
    dataService: DataService,
    private vendorSalesRepService: VendorSalesRepService
  ) {
    super({
      client: dataService,
      url: (env.api.backend.central.endpoint as CentralApis).activity,
    });

    this.tableFieldsToShown = [
      'knockStatus',
      'knockType',
      'addressLink',
      'createdAt',
      'createdByLink',
      'modifiedAt',
      'modifiedByLink',
    ];

    const keys = Object.assign({}, new Knock(), {
      modifiedByLink: 'link',
      salesRepLink: 'link',
      createdByLink: 'link',
      addressLink: 'link',
    });

    const baseKeys = Object.assign({}, new BaseKnock(), {
      modifiedByLink: 'link',
      salesRepLink: 'link',
      createdByLink: 'link',
      addressLink: 'link',
    });

    const editableFields = ['comments', 'knockStatus', 'knockType'];
    const dropdownFields = ['knockType', 'knockStatus'];
    const hiddenFormFields = ['addressId', 'createdBy', 'modifiedBy', 'href'];

    const knockColumns = this.initializeColumns({
      keys,
      editableFields,
      dropdownFields,
      hiddenFormFields,
    });
    const baseKnockColumns = this.initializeColumns({
      keys: baseKeys,
      editableFields,
      dropdownFields,
      hiddenFormFields,
    });

    this.columnsVisibility.next(knockColumns);
    this.baseColumnsVisibility.next(baseKnockColumns);
  }

  public get columns(): Observable<Columns[]> {
    return this.columnsVisibility.asObservable();
  }

  public get baseColumns(): Observable<Columns[]> {
    return this.baseColumnsVisibility.asObservable();
  }

  public get distinctColumns(): Observable<Columns[]> {
    return combineLatest([this.columns, this.baseColumns]).pipe(
      map((results) => {
        const filtered = results[0].filter(
          (column) =>
            !results[1].find(
              (secondColumn) => column.field === secondColumn.field
            )
        );

        return filtered;
      })
    );
  }

  getAll(queryParams?: any, headers?: any): Observable<Result<Knock>> {
    let knocksResult: Result<Knock>;

    /*if (!queryParams) {
      queryParams = {
        limit: 100,
      };
    }*/

    return super.getAll(queryParams, headers).pipe(
      take(1),
      concatMap((response) => {
        knocksResult = response;
        return this.fetchSalesReps(knocksResult.items);
      }),
      map((salesRepsResult: Result<VendorSalesRep>) => {
        const knocks = this.mapKnocks(knocksResult.items, salesRepsResult);
        knocksResult.items = knocks;

        return knocksResult;
      })
    );
  }

  getOne(queryParams: any, id?: string): Observable<Knock> {
    let knocks;
    return super.getOne(queryParams, id).pipe(
      take(1),
      concatMap((response: []) => {
        knocks = [response];
        return this.fetchSalesReps(knocks);
      }),
      map(
        (salesRepsResult: Result<VendorSalesRep>) =>
          this.mapKnocks(knocks, salesRepsResult)[0]
      )
    );
  }

  initializeColumns(
    config: ConfigColumns<any> = {
      keys: {} as Knock,
      hiddenFormFields: [],
      dropdownFields: [],
      editableFields: [],
    }
  ) {
    const { keys, dropdownFields, hiddenFormFields, editableFields } = config;
    const columns: string[] = Object.keys(keys) as string[];
    const columnsProperties: Columns[] = columns
      .filter(
        (column) => !column.startsWith('_') && column.toLowerCase() !== 'id'
      )
      .map((column) => ({
        field: column,
        visible: this.tableFieldsToShown.includes(column),
        type:
          dropdownFields && dropdownFields.includes(column)
            ? 'option'
            : this.getType(keys, column),
        hiddenFormField: hiddenFormFields
          ? hiddenFormFields.includes(column)
          : false,
        hiddenTableField: !this.tableFieldsToShown.includes(column),
        isEditable: editableFields ? editableFields.includes(column) : false,
      }))
      .sort(
        (a, b) =>
          this.tableFieldsToShown.indexOf(a.field) -
          this.tableFieldsToShown.indexOf(b.field)
      );
    //this.columnsVisibility.next(columnsProperties);
    return columnsProperties;
  }

  getType(keys, column: string) {
    const varType = typeof keys[column];
    if (keys[column] === 'raw' || keys[column] === 'link') {
      return keys[column];
    }
    switch (varType) {
      case 'object':
        const objType = Object.prototype.toString.call(keys[column]);

        switch (objType) {
          case '[object Date]':
            return 'date';
          case '[object Array]':
            return 'array';
          default:
            return 'object';
        }
      default:
        return varType;
    }
  }

  setColumns(columns: Columns[]): Observable<Columns[]> {
    return this.columns.pipe(
      take(1),
      switchMap((cols) => {
        if (!cols || cols.length <= 0) {
          return of(columns);
        } else {
          const newColumns = cols.map((col) => {
            const index = columns.findIndex((c) => c.field === col.field);
            col.visible = !columns[index]
              ? col.visible
              : columns[index].visible;

            return col;
          });

          return of(newColumns);
        }
      }),
      tap((cols) => {
        this.columnsVisibility.next(cols);
      })
    );
  }

  createFromDTO(item): Knock {
    return new Knock(item);
  }

  public fetchSalesReps(knocks) {
    //Get all possible vendorSalesRepsIds
    const salesRepsIds = this.getSalesRepIds(knocks);
    return this.vendorSalesRepService
      ? this.vendorSalesRepService
          .getAll(null, {
            condition: 'cpmsIDs',
            value: salesRepsIds.join(`','`).trim(),
          })
          .pipe(take(1))
      : of([]);
  }

  private getSalesRepIds(knocks) {
    const salesRepsIds = [];
    knocks.forEach((iKnock: IKnock) => {
      if (
        iKnock.vendorSalesRep &&
        iKnock.vendorSalesRep.indexOf(' ') === -1 &&
        !salesRepsIds.includes(iKnock.vendorSalesRep)
      ) {
        salesRepsIds.push(iKnock.vendorSalesRep);
      }

      if (
        iKnock.createdBy &&
        iKnock.createdBy.indexOf(' ') === -1 &&
        !Knock.isTelusMember(iKnock.createdBy) &&
        !salesRepsIds.includes(iKnock.createdBy)
      ) {
        salesRepsIds.push(iKnock.createdBy);
      }

      if (
        iKnock.modifiedBy &&
        iKnock.modifiedBy.indexOf(' ') === -1 &&
        !Knock.isTelusMember(iKnock.modifiedBy) &&
        !salesRepsIds.includes(iKnock.modifiedBy)
      ) {
        salesRepsIds.push(iKnock.modifiedBy);
      }
    });

    return salesRepsIds;
  }

  private mapKnocks(knocks, salesRepsResult) {
    const salesReps = salesRepsResult?.items;
    return knocks.map((knock) => {
      const extraInformation = salesReps
        ? {
            createdByData: salesReps.find(
              (salesRep) => salesRep.cpms === knock?.createdBy
            ),
            modifiedByData: salesReps.find(
              (salesRep) => salesRep.cpms === knock?.modifiedBy
            ),
            salesRepData: salesReps.find(
              (salesRep) => salesRep.cpms === knock?.vendorSalesRep
            ),
          }
        : undefined;

      return new Knock(knock, extraInformation);
    });
  }
}
